import { SK } from './skin';

// NOTIFICATION CENTER (owner QoL): every toast (hud.vitalGain) is also RECORDED here, so events you
// missed (base decay warnings, tool breaks, grants) can be re-read. A 🔔 bell (main mounts it on the
// inventory screen's header) shows the unread count; clicking it drops the list. Session-only.

interface Note { at: number; msg: string; color: number; }
const MAX_NOTES = 60;

// Toast callers pass raw hex colors; bucket each into the skin's semantic tones for the list
// (good / bad / warn -- anything else reads as body text).
function toneOf(color: number): string {
  const r = (color >> 16) & 255, g = (color >> 8) & 255, b = color & 255;
  if (g > r * 1.15 && g > b) return SK.good;
  if (r > g * 1.6) return SK.bad;
  if (b < g * 0.7 && r > b) return SK.warn;
  return SK.text;
}

export class NotifyCenter {
  private readonly notes: Note[] = [];
  private unread = 0;
  private readonly bell: HTMLButtonElement;
  private readonly panel: HTMLDivElement;
  private open = false;

  constructor() {
    this.bell = document.createElement('button');
    this.bell.style.cssText = `margin:0 0 8px 0;padding:4px 10px;background:${SK.surface2};border:1px solid ${SK.border}`
      + `;border-radius:${SK.rSm};color:${SK.text};font:600 ${SK.fsMicro} ${SK.font};font-variant-numeric:tabular-nums;cursor:pointer;position:relative`;
    this.panel = document.createElement('div');
    this.panel.style.cssText = 'position:fixed;z-index:60;display:none;flex-direction:column;gap:4px;max-height:300px;overflow-y:auto;'
      + `background:${SK.glass};border:1px solid ${SK.border};border-radius:${SK.rMd};padding:8px 12px;min-width:260px;`
      + `font-family:${SK.font};box-shadow:0 8px 30px rgba(0,0,0,0.6)`;
    document.body.appendChild(this.panel);
    this.bell.addEventListener('click', (e) => { e.stopPropagation(); this.toggle(); });
    window.addEventListener('pointerdown', (e) => { if (this.open && !this.panel.contains(e.target as Node) && e.target !== this.bell) this.hide(); });
    this.paintBell();
  }

  // The bell element for the host UI (main mounts it on the loadout header).
  get button(): HTMLButtonElement { return this.bell; }

  add(msg: string, color: number): void {
    this.notes.unshift({ at: Date.now(), msg, color });
    if (this.notes.length > MAX_NOTES) this.notes.pop();
    if (!this.open) this.unread = Math.min(99, this.unread + 1);
    this.paintBell();
    if (this.open) this.paintPanel();
  }

  hide(): void { this.open = false; this.panel.style.display = 'none'; }

  private toggle(): void {
    this.open = !this.open;
    if (this.open) {
      this.unread = 0;
      this.paintBell();
      const r = this.bell.getBoundingClientRect();
      this.panel.style.left = Math.max(8, Math.min(window.innerWidth - 290, r.left)) + 'px';
      this.panel.style.top = (r.bottom + 6) + 'px';
      this.panel.style.display = 'flex';
      this.paintPanel();
    } else this.hide();
  }

  private paintBell(): void {
    this.bell.textContent = this.unread > 0 ? `🔔 ${this.unread}` : '🔔';
    this.bell.style.color = this.unread > 0 ? SK.warn : SK.text; // unread = attention
  }

  private paintPanel(): void {
    this.panel.replaceChildren();
    if (!this.notes.length) {
      const empty = document.createElement('div');
      empty.textContent = 'Nothing yet.';
      empty.style.cssText = `font:${SK.fsMicro} ${SK.font};color:${SK.faint};padding:4px 0`;
      this.panel.appendChild(empty);
      return;
    }
    for (const n of this.notes) {
      const line = document.createElement('div');
      const t = new Date(n.at);
      const hh = String(t.getHours()).padStart(2, '0'), mm = String(t.getMinutes()).padStart(2, '0');
      line.style.cssText = `font:${SK.fsMicro}/1.5 ${SK.font};display:flex;gap:8px`;
      const time = document.createElement('span');
      time.textContent = `${hh}:${mm}`;
      time.style.cssText = `color:${SK.faint};font-variant-numeric:tabular-nums;flex:none`;
      const msg = document.createElement('span');
      msg.textContent = n.msg;
      msg.style.color = toneOf(n.color);
      line.append(time, msg);
      this.panel.appendChild(line);
    }
  }
}
